import { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { fetchSubscriptionBillingDataAction } from "../slices/subscriptionBillingDataSlice";
import { FetchCampaignStatusRequest } from "@/modules/administration/features/Stochastic/CampaignStatus/api/fetchCampaignStatus/types";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export function useSubscriptionBillingData() {
  const dispatch = useAppDispatch();

  const { campaigns, totalCount, loading, error } = useSelector(
    (state: RootState) => state.subscriptionBillingData,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [sorting, setSorting] = useState<SortingState>([]);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  const requestParams = useMemo<FetchCampaignStatusRequest>(() => {
    const params: FetchCampaignStatusRequest = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      sortOrder:
        sorting.length > 0 ? (sorting[0].desc ? "DESC" : "ASC") : "DESC",
      sortBy: sorting.length > 0 ? sorting[0].id : "id",
      billingType: "subscription",
    };

    if (filters.campaignStatusId) {
      params.campaignStatusId = filters.campaignStatusId as number;
    }

    if (filters.description) {
      params.description = filters.description as string;
    }

    return params;
  }, [pagination, sorting, filters]);

  useEffect(() => {
    dispatch(fetchSubscriptionBillingDataAction(requestParams));
  }, [dispatch, requestParams]);

  const handlePaginationChange: OnChangeFn<PaginationState> = useCallback(
    (updaterOrValue) => {
      setPagination((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleSortingChange: OnChangeFn<SortingState> = useCallback(
    (updaterOrValue) => {
      setSorting((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
    },
    [],
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  return {
    campaigns,
    loading,
    error,
    totalCount,
    pagination,
    sorting,
    filters,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
}
